import { useForm } from "react-hook-form";
import { useState } from "react";
import {
  FACFormDataTextFormat,
  phoneNumberWithUSACodeFormatting,
} from "js/formatting";
import { useSelector } from "react-redux";
import { submitFNOL } from "service/FNOL/submitFNOL";
import { useTranslation } from "react-i18next";

import LoadingOverlay from "react-loading-overlay";
import moment from "moment/moment";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";

export default function FACStepSix({
  formStep,
  setFormStep,
  setFACFormData,
  setConfirmation,
  setIsConfirmationDialogOpen,
  setOpenDialog,
  setTitle,
  setMessage,
  FACFormData,
}) {
  const { t } = useTranslation();
  LoadingOverlay.propTypes = undefined;
  const { register: FACStepSixRegister, handleSubmit: FACStepSixHandleSubmit } =
    useForm();

  const MAXIMUMWORDCOUNT = 300;
  const [currentWordCount, setCurrentWordCount] = useState(0);
  const { csrUser } = useSelector((s) => s.csrUser);
  const { user } = useSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState(false);

  const onError = () => {
    setIsLoading(false);
    setOpenDialog(true);
    setTitle("Unable to connect to server");
    setMessage("Server was unable to connect, please try again later.");
  };

  async function getFACFormDataString() {
    return new Promise((res, rej) => {
      res(FACFormDataTextFormat(FACFormData));
    });
  }

  function getFACObjectMapper() {
    return {
      fnolReceivedFrom: "Customer Portal",
      fnolId: null,
      informantsName: `${FACFormData?.firstNameClaimer} ${FACFormData?.lastNameClaimer}`,
      companyName: "",
      mailsFromToAddress: `${FACFormData?.mailingAddress} ${FACFormData?.mailingCity}, ${FACFormData?.mailingState} ${FACFormData?.mailingZip}`,
      mailFormsToName: `${FACFormData?.mailingFirstName} ${FACFormData?.mailingLastName}`,
      informantPhoneNumber: FACFormData?.phoneClaimer
        ? phoneNumberWithUSACodeFormatting(FACFormData?.phoneClaimer)
        : "",
      formsRequestedBy: "",
      informantCountry: "",
      informantFaxNumber: "",
      informantAddress1: FACFormData?.streetAddressClaimer ?? "",
      informantAddress2: FACFormData?.streetAddress2Claimer ?? "",
      informantCity: FACFormData?.cityClaimer ?? "",
      informantState: FACFormData?.stateClaimer ?? "",
      informantZip: FACFormData?.zipClaimer ?? "",
      informantEmail: FACFormData?.emailClaimer ?? "",
      relationshipDeceased: "Owner",
      mailFormsToCompanyName: FACFormData?.mailingCompanyName ?? "",
      deceasedFullName: `${FACFormData?.firstName} ${FACFormData?.lastName}`,
      deceasedFirstName: FACFormData?.firstName ?? "",
      deceasedLastName: FACFormData?.lastName ?? "",
      deceasedMiddleName: FACFormData?.middleName ?? "",
      deceasedId: FACFormData?.ssn ?? "",
      deceasedDateOfBirth: FACFormData?.dob
        ? moment(FACFormData?.dob).format("MM/DD/YYYY")
        : "",
      deceasedDateOfDeath: FACFormData?.dod
        ? moment(FACFormData?.dod).format("MM/DD/YYYY")
        : "",
      mannerOfDeath: FACFormData?.manner ?? "",
      foreignDeath: FACFormData?.foreign === "no" ? "false" : "true",
      whichCountry: FACFormData?.foreignCountry ?? "",
      fnolType: "D",
      fnolReceivedDate: moment().format("MM/DD/YYYY"),
      funeralHomeInvolved: FACFormData?.funeralHomeName ? "true" : "false",
      nameFuneralHome: FACFormData?.funeralHomeName ?? "",
      specialInstructions: FACFormData?.mailingComment ?? "",
      funeralPhoneNumber: FACFormData?.funeralHomePhoneNumber
        ? phoneNumberWithUSACodeFormatting(FACFormData?.funeralHomePhoneNumber)
        : "",
      funeralEmployeeName: "",
      veifiedByGoogle: "",
      funeralAssignment: "",
      amountAssignment: 0,
      deathCertificateRequest: "false",
      deathCertificateStatus: "",
      causeOfDeath: FACFormData?.manner ?? "",
      stateOfResidence: "",
      maritalStatus: FACFormData?.marital ?? "",
      nameOfSpouse:
        FACFormData?.marital === "married"
          ? FACFormData?.spouseName
          : FACFormData?.marital === "widowed"
          ? FACFormData?.deceasedSpouse
          : "",
      verifiersName: "",
      callerAgentOnPolicy: "false",
      personalDeliveryBeneficiary: "false",
      personalDeliveryCustomer: "false",
      agentDeliveryPreference: "false",
      policyIssuedCountry: "US",
      region: "US",
      dateOfVerification: "",
      fnolCorrelationId: moment().format("YYYY-MM-DD HH:MM:SS"),
      totalPolicyCount: 1,
    };
  }

  async function onSubmit(data) {
    setFACFormData((currentFormData) => {
      return {
        ...currentFormData,
        ...data,
      };
    });

    if (!csrUser?.userId) {
      setIsLoading(true);
      const formattedFACFormData = await getFACFormDataString();
      const fnolRequest = getFACObjectMapper();
      await submitFNOL(
        FACFormData?.policyNumber,
        formattedFACFormData,
        user.userId,
        fnolRequest
      )
        .then((response) => {
          console.log(response);
          setIsLoading(false);
          setConfirmation(response?.transactionId);
          setIsConfirmationDialogOpen(true);
          setOpenDialog(false);
        })
        .catch((error) => {
          console.error(error);
          onError();
        });
    } else {
      setOpenDialog(true);
      setTitle(t("FNOL-Login-server-csr-error"));
      setMessage(t("FNOL-Login-server-csr-error"));
      setFormStep(5);
    }
  }

  return (
    <>
      <EverlakeLoadingOverlay
        isLoading={isLoading}
        loadingText={t("AgentFACStepSix-loadingText")}
        spinnerSize="xs"
      >
        {formStep === 6 && (
          <>
            <div>
              <hr />
              <p className="text-sm">{t("FACCustomer-title")}</p>
              <form onSubmit={FACStepSixHandleSubmit(onSubmit)}>
                <div className="grid gap-4 grid-cols-1 lg:gap-6">
                  <div>
                    <label id="ID_messageLabel" htmlFor="ID_message">
                      Your message (Max. {MAXIMUMWORDCOUNT} Characters)
                    </label>
                    <textarea
                      id="ID_message"
                      type="text"
                      name="message"
                      aria-labelledby="ID_messageLabel"
                      aria-describedby="ID_messageError"
                      className="mt-1"
                      rows="6"
                      {...FACStepSixRegister("notes", {
                        maxLength: {
                          value: MAXIMUMWORDCOUNT,
                          message: t("AgentFACStepSix-maxLengthMessage"),
                        },
                        onChange: (e) => {
                          setCurrentWordCount(e.target.value.length);
                        },
                      })}
                    />
                    <span
                      className={
                        currentWordCount > MAXIMUMWORDCOUNT
                          ? "ui-input-error"
                          : ""
                      }
                    >
                      ({currentWordCount}/{MAXIMUMWORDCOUNT})
                    </span>
                  </div>

                  <div className="flex justify-end">
                    <button
                      className="ui-btn-secondary ui-btn-min-w mt-4"
                      type="button"
                      onClick={() => setFormStep(5)}
                    >
                      {t("FACStep-two-btn-1")}
                    </button>

                    <button className="ui-btn-primary ui-btn-min-w mt-4">
                      {t("FACStep-two-btn-2")}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </>
        )}
      </EverlakeLoadingOverlay>
    </>
  );
}
